import { useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Autocomplete,
  TextField,
  Tooltip,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import { useAuthStore } from '../../stores/authStore';
import { useExclusionsStore } from '../../stores/exclusionsStore';
import { useFilterStore } from '../../stores/filterStore';
import { formatOrgDisplayName } from '../../utils/display';
import type { FilterOption, UserFilterOption } from '../../types';

interface HeaderProps {
  height: number;
  onMenuClick: () => void;
  showMenuButton: boolean;
  sidebarWidth: number;
}

export default function Header({ height, onMenuClick, showMenuButton, sidebarWidth }: HeaderProps) {
  const { user } = useAuthStore();
  const {
    excludedOrgIds,
    excludedUserIds,
    availableOrgs,
    availableUsers,
    setExcludedOrgIds,
    setExcludedUserIds,
    initializeAll,
    clearExclusions,
  } = useExclusionsStore();
  const { availableOptions, isOptionsLoaded } = useFilterStore();

  const hasInitializedRef = useRef(false);

  // Sync filter options from filterStore to exclusionsStore when options are loaded
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!isOptionsLoaded) return;

    // Options loaded by AuthGuard, use them for exclusions atomically
    hasInitializedRef.current = true;
    if (availableOrgs.length === 0 || availableUsers.length === 0) {
      initializeAll(availableOptions.orgIds, availableOptions.userIds);
    }
  }, [isOptionsLoaded, availableOptions, availableOrgs, availableUsers, initializeAll]);

  const selectedOrgs = availableOrgs.filter((o) => excludedOrgIds.includes(o.id));
  const selectedUsers = availableUsers.filter((u) => excludedUserIds.includes(u.id));
  const hasExclusions = excludedOrgIds.length > 0 || excludedUserIds.length > 0;

  return (
    <AppBar
      position="fixed"
      sx={{
        height,
        ml: `${sidebarWidth}px`,
        width: `calc(100% - ${sidebarWidth}px)`,
        transition: 'margin 0.2s ease, width 0.2s ease',
        background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
        borderBottom: '1px solid rgba(139, 92, 246, 0.3)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      }}
    >
      <Toolbar sx={{ height: '100%', gap: 1.5 }}>
        {showMenuButton && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={onMenuClick}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography variant="h6" component="div" sx={{ fontWeight: 600, whiteSpace: 'nowrap', flexGrow: 1 }}>
          Product & CS Dashboard
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AutoGraphIcon />}
            onClick={() => window.open('/api/v1/reports/yearly-summary-2025', '_blank')}
            sx={{
              background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 50%, #3b82f6 100%)',
              fontWeight: 600,
              px: 2,
              py: 0.75,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #9333ea 0%, #4f46e5 50%, #2563eb 100%)',
                boxShadow: '0 6px 20px rgba(139, 92, 246, 0.5)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            2025 Summary
          </Button>
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={availableOrgs}
            getOptionLabel={(option: FilterOption) => formatOrgDisplayName(option.name)}
            value={selectedOrgs}
            onChange={(_, newValue) => setExcludedOrgIds(newValue.map((v) => v.id))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Exclude Orgs"
                placeholder={selectedOrgs.length === 0 ? 'None' : ''}
                sx={{
                  '& .MuiInputBase-root': {
                    bgcolor: 'rgba(0,0,0,0.2)',
                    flexWrap: 'nowrap',
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) => {
              if (value.length === 0) return null;
              const first = value[0];
              const rest = value.length - 1;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    {...getTagProps({ index: 0 })}
                    label={formatOrgDisplayName(first.name)}
                    size="small"
                    sx={{ bgcolor: 'rgba(239, 68, 68, 0.3)', maxWidth: 90 }}
                  />
                  {rest > 0 && (
                    <Chip
                      label={`+${rest}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(239, 68, 68, 0.2)', height: 22 }}
                    />
                  )}
                </Box>
              );
            }}
            sx={{ width: 200, flexShrink: 0 }}
          />

          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={availableUsers}
            getOptionLabel={(option: UserFilterOption) => option.name}
            value={selectedUsers}
            onChange={(_, newValue) => setExcludedUserIds(newValue.map((v) => v.id))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Exclude Users"
                placeholder={selectedUsers.length === 0 ? 'None' : ''}
                sx={{
                  '& .MuiInputBase-root': {
                    bgcolor: 'rgba(0,0,0,0.2)',
                    flexWrap: 'nowrap',
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) => {
              if (value.length === 0) return null;
              const first = value[0];
              const rest = value.length - 1;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    {...getTagProps({ index: 0 })}
                    label={first.name}
                    size="small"
                    sx={{ bgcolor: 'rgba(239, 68, 68, 0.3)', maxWidth: 90 }}
                  />
                  {rest > 0 && (
                    <Chip
                      label={`+${rest}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(239, 68, 68, 0.2)', height: 22 }}
                    />
                  )}
                </Box>
              );
            }}
            sx={{ width: 200, flexShrink: 0 }}
          />

          {hasExclusions && (
            <Tooltip title="Clear all exclusions">
              <IconButton
                size="small"
                onClick={clearExclusions}
                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#ef4444' } }}
              >
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2 }}>
            <Typography variant="body2" sx={{ opacity: 0.9, whiteSpace: 'nowrap' }}>
              {user.email}
            </Typography>
            <Button
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={() => window.dispatchEvent(new CustomEvent('auth:force-logout'))}
              sx={{ opacity: 0.9, '&:hover': { opacity: 1 } }}
            >
              Logout
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}
